import { createGroupActionFromFlow } from '../../../converters/helpers';
import type { FlowCardDefinition } from '../types';

const relayActions = [
	{ value: 'on', label: 'Turn on' },
	{ value: 'off', label: 'Turn off' },
	{ value: 'toggle', label: 'Toggle' },
];
const lightActions = [...relayActions, { value: 'set_brightness', label: 'Set brightness' }];
const rollerActions = [
	{ value: 'open', label: 'Open' },
	{ value: 'close', label: 'Close' },
	{ value: 'stop', label: 'Stop' },
	{ value: 'position', label: 'Move to position' },
];
const thermostatActions = [
	{ value: 'enable', label: 'Enable' },
	{ value: 'disable', label: 'Disable' },
	{ value: 'set_temperature', label: 'Set target temperature' },
];

const actionsByType: Record<string, typeof relayActions> = {
	relay: relayActions,
	light: lightActions,
	roller: rollerActions,
	thermostat: thermostatActions,
};

function groupSummary(data: Record<string, any>): string {
	if (!data.groupId) return 'Choose a group';
	const name = String(data.groupName || '').trim() || `Group ${data.groupId}`;
	if (data.action === 'position') return `Move ${name} to ${Number(data.rollerPosition ?? 100)}%`;
	if (data.action === 'set_brightness')
		return `Set ${name} brightness to ${Number(data.brightness ?? 100)}%`;
	if (data.action === 'set_temperature')
		return `Set ${name} target to ${Number(data.thermostatTemperature ?? 21)}°C`;
	const labels: Record<string, string> = {
		on: 'Turn on',
		off: 'Turn off',
		toggle: 'Toggle',
		open: 'Open',
		close: 'Close',
		stop: 'Stop',
		enable: 'Enable',
		disable: 'Disable',
	};
	return `${labels[data.action] || 'Control'} ${name}`;
}

function normalizeGroupAction(data: Record<string, any>): Record<string, any> {
	const groupType = String(data.groupType || 'relay');
	const options = actionsByType[groupType] || relayActions;
	const action = options.some((option) => option.value === data.action)
		? data.action
		: options[0].value;
	const normalized = { ...data, groupType, action };
	return { ...normalized, label: groupSummary(normalized) };
}

export const groupActionCard: FlowCardDefinition = {
	type: 'groupAction',
	version: '1.0.0',
	domain: 'core',
	role: 'action',
	category: 'actions',
	label: 'Group Action',
	description: 'Control every device in a compatible Shelly group',
	icon: 'fa-layer-group',
	color: '#a78bfa',
	schema: {
		$schema: 'https://json-schema.org/draft/2020-12/schema',
		type: 'object',
		required: ['groupId', 'action'],
		properties: {
			groupId: { type: 'string', minLength: 1 },
			groupName: { type: 'string' },
			groupType: { type: 'string', enum: ['relay', 'light', 'roller', 'thermostat'] },
			action: {
				type: 'string',
				enum: [
					...new Set(
						Object.values(actionsByType)
							.flat()
							.map((option) => option.value),
					),
				],
			},
			brightness: { type: 'number', minimum: 0, maximum: 100 },
			rollerPosition: { type: 'number', minimum: 0, maximum: 100 },
			thermostatTemperature: { type: 'number', minimum: 5, maximum: 35 },
			delay: {
				type: 'object',
				properties: {
					value: { type: 'number', minimum: 0 },
					units: { type: 'string', enum: ['sec', 'min'] },
				},
			},
		},
	},
	fields: [
		{
			path: 'groupId',
			label: 'Group',
			type: 'resource',
			resource: {
				kind: 'group',
				labelPath: 'groupName',
				typePath: 'groupType',
			},
		},
		{
			path: 'action',
			label: 'Action',
			type: 'select',
			optionsFrom: {
				path: 'groupType',
				cases: actionsByType,
				fallback: relayActions,
			},
		},
		{
			path: 'brightness',
			label: 'Brightness',
			type: 'number',
			min: 0,
			max: 100,
			step: 1,
			visibleWhen: { path: 'action', equals: 'set_brightness' },
		},
		{
			path: 'rollerPosition',
			label: 'Position',
			type: 'number',
			min: 0,
			max: 100,
			step: 1,
			visibleWhen: { path: 'action', equals: 'position' },
		},
		{
			path: 'thermostatTemperature',
			label: 'Target temperature',
			type: 'number',
			min: 5,
			max: 35,
			step: 0.5,
			visibleWhen: { path: 'action', equals: 'set_temperature' },
		},
		{ path: 'delay.value', label: 'Delay', type: 'number', min: 0, step: 1, group: 'Timing' },
		{
			path: 'delay.units',
			label: 'Delay unit',
			type: 'select',
			group: 'Timing',
			options: [
				{ value: 'sec', label: 'Seconds' },
				{ value: 'min', label: 'Minutes' },
			],
		},
	],
	ports: { sources: ['output'] },
	defaults: () => ({
		groupId: '',
		groupName: '',
		groupType: 'relay',
		action: 'on',
		brightness: 100,
		rollerPosition: 100,
		thermostatTemperature: 21,
		delay: { value: 0, units: 'sec' },
		cardVersion: '1.0.0',
	}),
	summary: groupSummary,
	normalize: normalizeGroupAction,
	validate: (data) =>
		String(data.groupId || '').trim() ? [] : [{ path: 'groupId', message: 'Select a group' }],
	compile: (node) => {
		if (node.data.sceneData) return { ...node.data.sceneData, _fid: node.id };
		const action = String(node.data.action || 'on');
		const groupType = String(node.data.groupType || 'relay');
		let actionName = 'turn';
		let actionValue: string | number | boolean = action;
		if (groupType === 'roller') {
			actionName = action === 'position' ? 'position' : 'direction';
			actionValue = action === 'position' ? Number(node.data.rollerPosition ?? 100) : action;
		} else if (groupType === 'thermostat') {
			actionName = action === 'set_temperature' ? 'target_t' : 'enable';
			actionValue =
				action === 'set_temperature'
					? Number(node.data.thermostatTemperature ?? 21)
					: action === 'enable';
		} else if (groupType === 'light' && action === 'set_brightness') {
			actionName = 'brightness';
			actionValue = Number(node.data.brightness ?? 100);
		}
		return {
			...createGroupActionFromFlow({
				groupId: String(node.data.groupId || ''),
				actionName,
				actionValue,
			}),
			_fid: node.id,
			_flow_card: 'groupAction',
			_flow_card_version: '1.0.0',
		};
	},
	migrate: (data) => {
		const firstAction = Array.isArray(data.actions) ? data.actions[0] : undefined;
		return normalizeGroupAction({
			...data,
			groupId: String(data.groupId ?? data.group_id ?? (data.sceneData as any)?.grpctrl ?? ''),
			groupName: data.groupName ?? '',
			groupType: data.groupType ?? 'relay',
			action: data.action ?? firstAction?.value ?? 'on',
			brightness: Number(data.brightness ?? 100),
			rollerPosition: Number(data.rollerPosition ?? 100),
			thermostatTemperature: Number(data.thermostatTemperature ?? 21),
			delay: data.delay ?? { value: 0, units: 'sec' },
		});
	},
};
